import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow, isValid } from 'date-fns';
import { clsx } from 'clsx';
import { WorkstationInfo } from '../services/api';
import { Card, Button, LoadingPage } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

// Safe date formatting helper
const safeFormatDistance = (dateStr: string | undefined | null): string => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    if (!isValid(date)) return '';
    return formatDistanceToNow(date, { addSuffix: true });
  } catch {
    return '';
  }
};

interface HistoryItem {
  id: string;
  orderId: string;
  orderNumber: string;
  itemNumber: number;
  productName: string;
  customerName: string;
  sidemark?: string;
  width: number;
  height: number;
  status: string;
  stepName: string;
  screenMaterial?: string;
  screenColor?: string;
  completedAt?: string;
  startedAt?: string;
  duration?: number | null;
  skippedReason?: string;
}

function normalizeHistoryItem(raw: any): HistoryItem {
  return {
    id: raw.id,
    orderId: raw.orderId,
    orderNumber: raw.orderNumber || '',
    itemNumber: raw.itemNumber || 1,
    productName: raw.productType || 'Keder Screen',
    customerName: raw.companyName || '',
    sidemark: raw.sidemark || '',
    width: raw.maxWidth || 0,
    height: raw.maxHeight || 0,
    status: raw.status,
    stepName: raw.workstationName || '',
    screenMaterial: raw.fabricType || '',
    screenColor: raw.fabricColor || '',
    completedAt: raw.completedAt,
    startedAt: raw.startedAt,
    duration: raw.duration || null,
    skippedReason: raw.skippedReason || '',
  };
}

export function HistoryPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [workstations, setWorkstations] = useState<WorkstationInfo[]>([]);
  const [selectedWorkstation, setSelectedWorkstation] = useState<string>('');
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState<'today' | 'week' | 'month'>('week');

  const loadWorkstations = useCallback(async () => {
    try {
      const response = await api.getWorkstations();
      if (!Array.isArray(response) || response.length === 0) {
        setWorkstations([]);
        setLoading(false);
        return;
      }

      // Filter workstations by user's assigned workstation_types
      const userWorkstationTypes = (user as any)?.permissions?.workstation_types || [];
      const hasAllAccess = (user as any)?.permissions?.all_access || userWorkstationTypes.length === 0;

      let assignedWorkstations = response;
      if (!hasAllAccess && userWorkstationTypes.length > 0) {
        assignedWorkstations = response.filter((ws: any) => {
          const wsId = (ws.id || '').toLowerCase();
          const wsCode = (ws.code || '').toLowerCase();
          const wsName = (ws.name || '').toLowerCase();
          return userWorkstationTypes.some((type: string) => {
            const typeLower = type.toLowerCase();
            return wsId === typeLower || wsCode === typeLower ||
                   wsCode.includes(typeLower) || typeLower.includes(wsCode) ||
                   wsName.includes(typeLower);
          });
        });
      }

      setWorkstations(assignedWorkstations);
      if (assignedWorkstations.length > 0 && !selectedWorkstation) {
        setSelectedWorkstation(assignedWorkstations[0].id);
      }
    } catch (error) {
      console.error('Failed to load workstations:', error);
    } finally {
      setLoading(false);
    }
  }, [user, selectedWorkstation]);

  const loadHistory = useCallback(async () => {
    if (!selectedWorkstation) return;
    setHistoryLoading(true);
    try {
      const data = await api.getHistory(selectedWorkstation);
      const items = Array.isArray(data) ? data.map(normalizeHistoryItem) : [];

      // Filter by date
      const now = new Date();
      const filtered = items.filter(item => {
        const itemDate = new Date(item.completedAt || '');
        if (!isValid(itemDate)) return true; // show items without dates
        if (dateFilter === 'today') {
          return itemDate.toDateString() === now.toDateString();
        } else if (dateFilter === 'week') {
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          return itemDate >= weekAgo;
        }
        // month
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        return itemDate >= monthAgo;
      });

      setHistory(filtered);
    } catch (error) {
      console.error('Failed to load history:', error);
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [selectedWorkstation, dateFilter]);

  useEffect(() => {
    loadWorkstations();
  }, [loadWorkstations]);

  useEffect(() => {
    if (selectedWorkstation) {
      loadHistory();
    }
  }, [selectedWorkstation, dateFilter, loadHistory]);

  if (loading) {
    return <LoadingPage />;
  }

  // Calculate stats
  const completedCount = history.filter(item => item.status === 'completed').length;
  const skippedCount = history.filter(item => item.status === 'skipped').length;
  const itemsWithDuration = history.filter(item => item.duration && item.duration > 0);
  const totalDuration = itemsWithDuration.reduce((sum, item) => sum + (item.duration || 0), 0);
  const avgDuration = itemsWithDuration.length > 0 ? Math.round(totalDuration / itemsWithDuration.length) : 0;

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-white">History</h1>

      {/* Workstation Selector */}
      {workstations.length > 0 ? (
        <Card>
          <label className="block text-sm font-medium text-gray-400 mb-2">
            Select Workstation
          </label>
          <select
            value={selectedWorkstation}
            onChange={(e) => setSelectedWorkstation(e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white text-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
          >
            {workstations.map((ws) => (
              <option key={ws.id} value={ws.id}>
                {ws.name}
              </option>
            ))}
          </select>
        </Card>
      ) : (
        <Card className="text-center py-8">
          <div className="text-gray-400">No workstations assigned</div>
        </Card>
      )}

      {/* Date Filter */}
      <div className="flex gap-2">
        {(['today', 'week', 'month'] as const).map((filter) => (
          <button
            key={filter}
            onClick={() => setDateFilter(filter)}
            className={clsx(
              'flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors',
              dateFilter === filter
                ? 'bg-yellow-400 text-gray-900'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            )}
          >
            {filter === 'today' ? 'Today' : filter === 'week' ? 'This Week' : 'This Month'}
          </button>
        ))}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="text-center">
          <div className="text-2xl font-bold text-green-400">{completedCount}</div>
          <div className="text-xs text-gray-400">Completed</div>
        </Card>
        <Card className="text-center">
          <div className="text-2xl font-bold text-gray-400">{skippedCount}</div>
          <div className="text-xs text-gray-400">Skipped</div>
        </Card>
        <Card className="text-center">
          <div className="text-2xl font-bold text-yellow-400">{avgDuration > 0 ? `${avgDuration}m` : '--'}</div>
          <div className="text-xs text-gray-400">Avg Time</div>
        </Card>
      </div>

      {/* Loading indicator for history */}
      {historyLoading && (
        <div className="flex justify-center py-4">
          <div className="text-gray-400 text-sm animate-pulse">Loading history...</div>
        </div>
      )}

      {/* History List */}
      {!historyLoading && (
        <div className="space-y-3">
          {history.map((item) => (
            <HistoryCard
              key={item.id}
              item={item}
              onViewDetails={() => navigate(`/work/${item.id}`)}
            />
          ))}
        </div>
      )}

      {!historyLoading && history.length === 0 && (
        <Card className="text-center py-12">
          <div className="text-gray-400 text-lg">No history found</div>
          <div className="text-gray-500 text-sm mt-2">
            Complete some work items to see them here
          </div>
        </Card>
      )}
    </div>
  );
}

interface HistoryCardProps {
  item: HistoryItem;
  onViewDetails?: () => void;
}

function HistoryCard({ item, onViewDetails }: HistoryCardProps) {
  const isSkipped = item.status === 'skipped';

  return (
    <Card className={clsx(
      'border-l-4',
      isSkipped ? 'border-l-gray-500 opacity-60' : 'border-l-green-500'
    )}>
      {/* Header */}
      <div className="flex justify-between items-start mb-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-yellow-400 font-semibold">{item.orderNumber}</span>
            {isSkipped && (
              <span className="px-2 py-0.5 bg-gray-700 text-gray-400 text-xs rounded">SKIPPED</span>
            )}
          </div>
          <div className="text-sm text-gray-500">Item {item.itemNumber} &bull; {item.stepName}</div>
        </div>
        <div className="text-right">
          <div className="text-sm text-gray-400">
            {safeFormatDistance(item.completedAt)}
          </div>
          {!isSkipped && item.duration != null && item.duration > 0 && (
            <div className="text-xs text-green-400">{item.duration} min</div>
          )}
        </div>
      </div>

      {/* Product Info */}
      <div className="mb-2">
        <div className="font-medium text-white">{item.productName}</div>
        <div className="text-sm text-gray-400">
          {item.customerName}
          {item.sidemark ? ` \u2022 ${item.sidemark}` : ''}
        </div>
      </div>

      {/* Details */}
      <div className="flex justify-between items-center text-sm">
        <div className="flex gap-4">
          {(item.width > 0 || item.height > 0) && (
            <div>
              <span className="text-gray-500">Size:</span>
              <span className="text-gray-300 ml-1">{item.width}" x {item.height}"</span>
            </div>
          )}
          {item.screenMaterial && (
            <div>
              <span className="text-gray-500">Material:</span>
              <span className="text-gray-300 ml-1">{item.screenMaterial}</span>
            </div>
          )}
        </div>
        <Button variant="secondary" onClick={onViewDetails}>
          Details
        </Button>
      </div>

      {/* Skipped reason */}
      {isSkipped && item.skippedReason && (
        <div className="text-xs text-gray-500 mt-2">
          Reason: {item.skippedReason}
        </div>
      )}
    </Card>
  );
}
